// Read hook: transactions, as typed domain objects.
//
// The read is role-scoped: an admin pulls the whole register via the Data Connect
// SDK; everyone else goes through the server-side scoped proxy (`fetchMyRegister`),
// keyed off the verified token email so a reviewer cannot widen it. The proxy
// returns each caller's HOME DIVISION ∪ their own allocations (scopeRegisterForGrant,
// admin-api) — cross-division roles (manager/dataCoordinator) get everything. A
// reviewer/team-lead with NO grant division collapses to allocations only (see the
// AccessGrantEditor division guard). The scoped read hooks share one query key.
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { fetchTransactions, fetchTransactionsForManage, fetchMyRegister, type ScopedRegister } from '../lib/db';
import { useAuth } from '../lib/auth';
import type { Transaction } from '../data/types';

export function useTransactions() {
  // Use the REAL role for the fetch decision: an admin reads the full register
  // (and, while previewing as another role, the page scopes it client-side via the
  // impersonated grant — so there is no refetch on toggle).
  const { realRole, user } = useAuth();
  const isAdmin = realRole === 'admin';
  const email = user?.email ?? '';
  return useQuery<Transaction[] | ScopedRegister, Error, Transaction[]>({
    queryKey: isAdmin ? queryKeys.transactions : queryKeys.scopedRegisterFor(email),
    queryFn: isAdmin ? fetchTransactions : fetchMyRegister,
    select: (d) => (isAdmin ? (d as Transaction[]) : (d as ScopedRegister).transactions),
    enabled: isAdmin || (!!realRole && email.length > 0),
  });
}

/**
 * Admin-only Manage read: the whole population INCLUDING soft-deleted rows, so the
 * Manage screen can show and restore removed references. Separate query key from
 * the register so the two caches don't collide; enabled only for a real admin.
 */
export function useManageTransactions() {
  const { realRole } = useAuth();
  const isAdmin = realRole === 'admin';
  return useQuery<Transaction[], Error>({
    queryKey: queryKeys.manageTransactions,
    queryFn: fetchTransactionsForManage,
    enabled: isAdmin,
  });
}
